import React from 'react';
import { Box, Container, Typography } from '@mui/material';
import { useResponsive } from '../../hooks/useResponsive';

interface AuthLayoutProps {
  children: React.ReactNode;
  title?: string;
  subtitle?: string;
}

export const AuthLayout: React.FC<AuthLayoutProps> = ({ 
  children, 
  title = "Добро пожаловать!", 
  subtitle = "Для начала выберите один из двух направлений" 
}) => {
  const { isMobile, isDesktop } = useResponsive();

  return (
    <Box
      sx={{
        minHeight: '100vh',
        display: 'flex',
        background: isMobile 
          ? 'linear-gradient(135deg, #667eea 0%, #764ba2 100%)'
          : 'linear-gradient(135deg, #667eea 0%, #764ba2 100%)',
      }}
    >
      {/* Левая часть с цитатой (только для desktop) */}
      {isDesktop && (
        <Box
          sx={{
            flex: 1,
            display: 'flex',
            flexDirection: 'column',
            justifyContent: 'space-between',
            p: 6,
            color: 'white',
            position: 'relative',
          }}
        >
          {/* Логотип */}
          <Box>
            <Typography 
              variant="h3" 
              sx={{ 
                fontWeight: 'bold',
                fontSize: '2rem',
                mb: 1,
                fontFamily: 'monospace'
              }}
            >
              EAZZY
            </Typography>
          </Box>

          {/* Цитата */}
          <Box sx={{ maxWidth: 400 }}>
            <Typography 
              variant="h4" 
              sx={{ 
                fontWeight: 300,
                lineHeight: 1.3,
                mb: 4,
                fontSize: '1.8rem'
              }}
            >
              Выберите работу по душе — и вам не придётся работать ни дня в жизни.
            </Typography>
            
            <Typography 
              variant="subtitle1" 
              sx={{ 
                opacity: 0.8,
                fontStyle: 'italic'
              }}
            >
              Конфуций
            </Typography>
          </Box>

          {/* Декоративные элементы */}
          <Box
            sx={{
              position: 'absolute',
              top: '20%',
              left: '10%',
              fontSize: '8rem',
              opacity: 0.1,
              fontFamily: 'serif',
              transform: 'rotate(-15deg)',
            }}
          >
            "
          </Box>
          <Box
            sx={{
              position: 'absolute',
              bottom: '30%',
              right: '10%',
              fontSize: '8rem',
              opacity: 0.1,
              fontFamily: 'serif',
              transform: 'rotate(15deg)',
            }}
          >
            "
          </Box>
        </Box>
      )}

      {/* Правая часть с формой */}
      <Box
        sx={{
          flex: isMobile ? 1 : 1,
          backgroundColor: 'white',
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          p: isMobile ? 2 : 4,
          borderRadius: isMobile ? 0 : '24px 0 0 24px',
        }}
      >
        <Container 
          maxWidth="sm" 
          sx={{ 
            width: '100%',
            maxWidth: isMobile ? '100%' : '400px'
          }}
        >
          {/* Заголовок */}
          <Box sx={{ textAlign: 'center', mb: 4 }}>
            {isMobile && (
              <Typography 
                variant="h4" 
                sx={{ 
                  fontWeight: 'bold',
                  mb: 2,
                  background: 'linear-gradient(135deg, #667eea 0%, #764ba2 100%)',
                  backgroundClip: 'text',
                  WebkitBackgroundClip: 'text',
                  WebkitTextFillColor: 'transparent',
                  fontFamily: 'monospace'
                }}
              >
                EAZZY
              </Typography>
            )}
            
            <Typography 
              variant={isMobile ? "h5" : "h4"} 
              sx={{ 
                fontWeight: 'bold',
                color: '#1a1a1a',
                mb: 1
              }}
            >
              {title}
            </Typography>
            
            <Typography 
              variant="body1" 
              sx={{ 
                color: '#666',
                fontSize: isMobile ? '0.9rem' : '1rem'
              }}
            >
              {subtitle}
            </Typography>
          </Box>

          {/* Контент */}
          {children}

          {/* Ссылка на регистрацию (если нужна) */}
          <Box sx={{ textAlign: 'center', mt: 3 }}>
            <Typography variant="body2" color="text.secondary">
              Нет аккаунта?{' '}
              <Typography 
                component="span" 
                sx={{ 
                  color: '#667eea',
                  cursor: 'pointer',
                  textDecoration: 'underline'
                }}
              >
                Зарегистрироваться
              </Typography>
            </Typography>
          </Box>
        </Container>
      </Box>
    </Box>
  );
};
